/**
 * Utility functions for calculating student scores and averages
 */

/**
 * Get grade weight for weighted average calculation
 * @param {string} grade - The grade level
 * @returns {number} - The weight for this grade
 */
const getGradeWeight = (grade) => {
  switch (grade) {
    case '9':
    case '9th':
    case '9th Grade':
      return 1;
    case '10':
    case '10th':
    case '10th Grade':
      return 2;
    case '11':
    case '11th':
    case '11th Grade':
      return 3;
    case '12':
    case '12th':
    case '12th Grade':
      return 4;
    case 'Portfolio Defence':
    case 'Portfolio Defense':
    case 'Portfolio':
      return 5;
    default:
      return 1; // Default weight for unknown grades
  }
};

/**
 * Calculate weighted average score for a specific student and tag combination
 * @param {string} studentId - The student's ID
 * @param {string} tag - The skill/tag name
 * @param {Array} tagFeedbacks - Array of tag feedback objects
 * @param {Array} posts - Array of post objects (needed to get grade information)
 * @returns {number|null} - The weighted average score rounded to 1 decimal place, or null if no scores
 */
export const getStudentTagAverage = (studentId, tag, tagFeedbacks, posts = []) => {
  const feedbacks = tagFeedbacks.filter(tf => 
    tf.studentId === studentId && 
    tf.tag === tag && 
    typeof tf.score === 'number'
  );
  
  if (feedbacks.length === 0) return null;
  
  // Calculate weighted average
  let totalWeightedScore = 0;
  let totalWeight = 0;
  
  feedbacks.forEach(tf => {
    // Find the post to get grade information
    const post = posts.find(p => p.id === tf.postId);
    const grade = post?.grade || 'Unknown';
    const weight = getGradeWeight(grade);
    
    totalWeightedScore += tf.score * weight;
    totalWeight += weight;
  });
  
  if (totalWeight === 0) return null;
  
  return Math.round((totalWeightedScore / totalWeight) * 10) / 10;
};

/**
 * Calculate weighted skill averages for a student across all skills
 * @param {string} studentId - The student's ID
 * @param {Array} tags - Array of all available tags/skills
 * @param {Array} tagFeedbacks - Array of tag feedback objects
 * @param {Array} posts - Array of post objects (needed to get grade information)
 * @returns {Array} - Array of objects with skill, average, and count properties
 */
export const getStudentSkillAverages = (studentId, tags, tagFeedbacks, posts = []) => {
  // Get all unique skills from tags
  const allSkills = [...new Set(tags)];
  
  // Calculate weighted averages for each skill
  const skillAverages = allSkills.map(skill => {
    const skillFeedbacks = tagFeedbacks.filter(tf => 
      tf.studentId === studentId && tf.tag === skill && typeof tf.score === 'number'
    );
    
    let average = null;
    if (skillFeedbacks.length > 0) {
      // Calculate weighted average
      let totalWeightedScore = 0;
      let totalWeight = 0;
      
      skillFeedbacks.forEach(tf => {
        // Find the post to get grade information
        const post = posts.find(p => p.id === tf.postId);
        const grade = post?.grade || 'Unknown';
        const weight = getGradeWeight(grade);
        
        totalWeightedScore += tf.score * weight;
        totalWeight += weight;
      });
      
      if (totalWeight > 0) {
        average = Math.round((totalWeightedScore / totalWeight) * 10) / 10;
      }
    }
    
    return {
      skill,
      average,
      count: skillFeedbacks.length
    };
  });
  
  // Sort by tag order (as defined in tags array)
  skillAverages.sort((a, b) => {
    const aIndex = tags.indexOf(a.skill);
    const bIndex = tags.indexOf(b.skill);
    return aIndex - bIndex;
  });
  
  return skillAverages;
};
